import { BANNER_ASPECT, BANNER_PIXELS } from '@/lib/plate';
import type { MediaKind } from '@/services/media.service';
import type { ContentStatus } from '@/types/api';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'multi-select'
  | 'checkbox'
  | 'tag-chips'
  | 'json'
  | 'image'
  | 'employee-select'
  | 'repeater'
  | 'paragraph-list'
  | 'map-point'
  | 'stakeholder-tree';

export interface RepeaterColumn {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'checkbox';
  placeholder?: string;
}

export interface ResourceField {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  help?: string;
  wide?: boolean;
  createOnly?: boolean;
  rows?: number;
  columns?: RepeaterColumn[];
  section?: 'details' | 'publication';
  pairField?: string;
  hidden?: boolean;
  aspectRatio?: number;
  lockAspect?: boolean;
  mediaKind?: MediaKind;
  helpPoints?: readonly string[];
}

export interface ResourceColumn {
  key: string;
  label: string;
  kind?: 'text' | 'status' | 'badge' | 'image';
  align?: 'right';
  muted?: boolean;
  sortable?: boolean;
}

export interface ResourceFilter {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  dynamic?: 'employee-leader' | 'asset';
}

export interface ResourceDescriptor {
  key: string;
  path: string;
  listPath?: string;
  permission: string;
  title: string;
  description: string;
  breadcrumb: string;
  singular: string;
  labelField: string;
  columns: ResourceColumn[];
  fields: ResourceField[];
  filters?: ResourceFilter[];
  publishable?: boolean;
  readOnly?: boolean;
  detailFromList?: boolean;
  extraAction?: { label: string; to: string };
}

const ENTITY_OPTIONS = [
  { value: 'SKT', label: 'Searah Ketapang (SKT)' },
  { value: 'SMB', label: 'Searah Muara Bakau (SMB)' },
  { value: 'SMY', label: 'Searah Malaysia (SMY)' },
];

export const VISIBILITY_OPTIONS = [
  { value: 'INTRANET', label: 'Intranet' },
  { value: 'INTERNET', label: 'Internet' },
  { value: 'BOTH', label: 'Both' },
];

const STAKEHOLDER_CATEGORY_OPTIONS = [
  { value: 'GOVERNMENT_BODY', label: 'Government Body' },
  { value: 'REGULATORY_BODY', label: 'Regulatory Body' },
  { value: 'PARTNER', label: 'Partner' },
];

const GROUP_ENTITY_OPTIONS = [{ value: 'GROUP', label: 'Group-wide' }, ...ENTITY_OPTIONS];

const LIFECYCLE_OPTIONS = [
  { value: 'Producing', label: 'Producing' },
  { value: 'Development', label: 'Development' },
  { value: 'Exploration', label: 'Exploration' },
  { value: 'Operated by Searah', label: 'Operated by Searah' },
  { value: 'Operated by Partner', label: 'Operated by Partner' },
];

const ASSET_TYPE_OPTIONS = [
  { value: 'Offshore', label: 'Offshore' },
  { value: 'Onshore', label: 'Onshore' },
  { value: 'Deepwater', label: 'Deepwater' },
  { value: 'LNG', label: 'LNG' },
];

const displayOrderField: ResourceField = {
  name: 'displayOrder',
  label: 'Display order',
  type: 'number',
  help: 'Lower numbers come first.',
};

const entity: ResourceDescriptor = {
  key: 'entities',
  path: '/private/entities',
  permission: 'entity',
  title: 'Operating entities',
  description:
    'The operating companies. Their code is the join key for assets, newsletters, production and the directory - set it once and never change it.',
  breadcrumb: 'Entities',
  singular: 'entity',
  labelField: 'name',
  publishable: true,
  filters: [{ name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS }],
  columns: [
    { key: 'code', label: 'Code', kind: 'badge' },
    { key: 'name', label: 'Name' },
    { key: 'headquarters', label: 'Headquarters', muted: true },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    {
      name: 'code',
      label: 'Entity code',
      type: 'text',
      required: true,
      createOnly: true,
      placeholder: 'SKT',
      help: 'Uppercase. Assets, articles and staff all point at this.',
    },
    { name: 'name', label: 'Short name', type: 'text', required: true, placeholder: 'Searah Ketapang' },
    { name: 'fullName', label: 'Legal name', type: 'text', required: true },
    { name: 'tagline', label: 'Tagline', type: 'text', wide: true },
    { name: 'established', label: 'Established', type: 'text', placeholder: '2024' },
    { name: 'headquarters', label: 'Headquarters', type: 'text' },
    { name: 'employeeCount', label: 'Employees', type: 'number' },
    { name: 'accent', label: 'Accent colour', type: 'text', placeholder: '#00649D' },
    { name: 'heroIntranetUrl', label: 'Hero image (Intranet)', type: 'image' },
    { name: 'heroIntranetAlt', label: 'Hero alt text (Intranet)', type: 'text' },
    { name: 'heroInternetUrl', label: 'Hero image (Internet)', type: 'image' },
    { name: 'heroInternetAlt', label: 'Hero alt text (Internet)', type: 'text' },
    { name: 'summary', label: 'Summary', type: 'textarea', wide: true, rows: 3 },
    {
      name: 'overview',
      label: 'Overview paragraphs',
      type: 'paragraph-list',
      wide: true,
    },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
    displayOrderField,
  ],
};

const asset: ResourceDescriptor = {
  key: 'asset-opco',
  path: '/private/assets',
  permission: 'asset',
  title: 'Asset register',
  description:
    'Every field and facility the group operates. Coordinates place the marker on the map, so check them against the coastline before publishing.',
  breadcrumb: 'Assets',
  singular: 'asset',
  labelField: 'name',
  publishable: true,
  filters: [
    { name: 'entityCode', label: 'Entity', options: ENTITY_OPTIONS },
    { name: 'lifecycle', label: 'Lifecycle', options: LIFECYCLE_OPTIONS },
    { name: 'type', label: 'Type', options: ASSET_TYPE_OPTIONS },
    { name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS },
  ],
  columns: [
    { key: 'name', label: 'Asset' },
    { key: 'entityCode', label: 'Entity', kind: 'badge' },
    { key: 'lifecycle', label: 'Lifecycle' },
    { key: 'region', label: 'Region', muted: true },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'entityCode', label: 'Operating entity', type: 'select', required: true, options: ENTITY_OPTIONS },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      required: true,
      options: [
        { value: 'Indonesia', label: 'Indonesia' },
        { value: 'Malaysia', label: 'Malaysia' },
      ],
    },
    { name: 'region', label: 'Region', type: 'text', required: true },
    { name: 'type', label: 'Type', type: 'select', required: true, options: ASSET_TYPE_OPTIONS },
    { name: 'lifecycle', label: 'Lifecycle', type: 'multi-select', required: true, options: LIFECYCLE_OPTIONS },
    { name: 'operator', label: 'Operator', type: 'text' },
    { name: 'workingInterest', label: 'Working interest (%)', type: 'number' },
    { name: 'production', label: 'Production', type: 'text', placeholder: '68,400 BOE/D' },
    { name: 'reserves', label: 'Reserves', type: 'text', placeholder: '412 MMBOE (2P)' },
    { name: 'waterDepth', label: 'Water depth', type: 'text' },
    { name: 'discoveredIn', label: 'Discovered', type: 'text' },
    { name: 'onstream', label: 'Onstream', type: 'text' },
    {
      name: 'latitude',
      label: 'Location',
      type: 'map-point',
      required: true,
      pairField: 'longitude',
      wide: true,
      help: 'Click the map or drag the pin, or type coordinates manually.',
    },
    { name: 'longitude', label: 'Longitude', type: 'number', required: true, hidden: true },
    { name: 'imageUrl', label: 'Image', type: 'image' },
    { name: 'imageAlt', label: 'Image alt text', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 4 },
    { name: 'highlights', label: 'Highlights', type: 'tag-chips', wide: true, placeholder: 'Type and press Enter…' },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
    displayOrderField,
  ],
};

const employee: ResourceDescriptor = {
  key: 'employees',
  path: '/private/employees',
  permission: 'employee',
  title: 'Staff directory',
  description:
    'The people directory, and the reporting lines the organisation chart is built from. "Reports to" holds a colleague’s code, not their name.',
  breadcrumb: 'Directory',
  singular: 'employee',
  labelField: 'name',
  publishable: true,
  filters: [
    { name: 'entityCode', label: 'Entity', options: [...ENTITY_OPTIONS, { value: 'GROUP', label: 'Group' }] },
    {
      name: 'role',
      label: 'Role',
      options: [
        { value: 'EMPLOYEE', label: 'Employee' },
        { value: 'LEADER', label: 'Leader' },
      ],
    },
    { name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS },
  ],
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'position', label: 'Position', muted: true },
    { key: 'department', label: 'Department' },
    { key: 'entityCode', label: 'Entity', kind: 'badge' },
    { key: 'role', label: 'Role', kind: 'badge' },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    {
      name: 'code',
      label: 'Directory code',
      type: 'text',
      required: true,
      createOnly: true,
      placeholder: 'emp-001',
      help: 'Reporting lines point at this. Lowercase and hyphenated.',
    },
    { name: 'name', label: 'Full name', type: 'text', required: true },
    { name: 'position', label: 'Position', type: 'text', required: true },
    { name: 'department', label: 'Department', type: 'text', required: true },
    {
      name: 'entityCode',
      label: 'Entity',
      type: 'select',
      required: true,
      options: [{ value: 'GROUP', label: 'Group (corporate)' }, ...ENTITY_OPTIONS],
    },
    { name: 'location', label: 'Location', type: 'text', required: true },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      required: true,
      options: [
        { value: 'EMPLOYEE', label: 'Employee' },
        { value: 'LEADER', label: 'Leader' },
      ],
      help: 'Leader profiles appear on the About page and on their entity profile.',
    },
    {
      name: 'groupKey',
      label: 'Leadership band key',
      type: 'text',
      help: 'Only used when Role is Leader. Matches a leadership band, e.g. opco-ceos. Leave blank to list under the entity instead.',
    },
    { name: 'email', label: 'Email', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'photoUrl', label: 'Portrait', type: 'image' },
    { name: 'photoAlt', label: 'Portrait alt text', type: 'text' },
    {
      name: 'reportsToCode',
      label: 'Reports to',
      type: 'text',
      placeholder: 'emp-001',
      help: 'The manager’s directory code. Leave blank for the group chief executive.',
    },
    { name: 'startedIn', label: 'Started in', type: 'text', placeholder: '2024' },
    { name: 'biography', label: 'Biography', type: 'textarea', wide: true, rows: 5 },
    { name: 'skills', label: 'Skills', type: 'tag-chips', wide: true, placeholder: 'Type and press Enter…' },
    { name: 'expertise', label: 'Expertise', type: 'tag-chips', wide: true, placeholder: 'Type and press Enter…' },
    {
      name: 'projects',
      label: 'Projects',
      type: 'repeater',
      wide: true,
      columns: [
        { name: 'name', label: 'Name' },
        { name: 'role', label: 'Role' },
        { name: 'year', label: 'Year' },
      ],
    },
    {
      name: 'social',
      label: 'Social links',
      type: 'repeater',
      wide: true,
      columns: [
        { name: 'label', label: 'Label' },
        { name: 'href', label: 'URL' },
        { name: 'icon', label: 'Icon' },
      ],
    },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
    displayOrderField,
  ],
};

const leadershipGroup: ResourceDescriptor = {
  key: 'leadership-groups',
  path: '/private/employees/groups',
  permission: 'leadershipGroup',
  title: 'Leadership bands',
  description:
    'The headings profiles are grouped under. Deleting a band does not delete its people - they fall back to their entity listing.',
  breadcrumb: 'Bands',
  singular: 'band',
  labelField: 'titleStrong',
  publishable: true,
  detailFromList: true,
  columns: [
    { key: 'key', label: 'Key', kind: 'badge' },
    { key: 'titleLead', label: 'Lead line' },
    { key: 'titleStrong', label: 'Strong line' },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'key', label: 'Band key', type: 'text', required: true, createOnly: true, placeholder: 'opco-ceos' },
    { name: 'titleLead', label: 'Lead line', type: 'text', required: true, help: 'Rendered in regular weight.' },
    { name: 'titleStrong', label: 'Strong line', type: 'text', required: true },
    displayOrderField,
  ],
};

const resource: ResourceDescriptor = {
  key: 'resources',
  path: '/private/resources',
  permission: 'resource',
  title: 'Resources',
  description:
    'Documents, templates and brand assets in the knowledge hub. "Restricted" excludes the record from the public API entirely - but the file URL itself is still not access-controlled, so a leaked link remains reachable.',
  breadcrumb: 'Resources',
  singular: 'resource',
  labelField: 'title',
  publishable: true,
  filters: [{ name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS }],
  columns: [
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category', kind: 'badge' },
    { key: 'type', label: 'Type', muted: true },
    { key: 'size', label: 'Size', align: 'right', muted: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'title', label: 'Title', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'text', required: true, placeholder: 'Policies' },
    { name: 'type', label: 'File type', type: 'text', required: true, placeholder: 'PDF' },
    { name: 'size', label: 'Size', type: 'text', placeholder: '2.4 MB' },
    { name: 'url', label: 'File URL', type: 'text', required: true },
    { name: 'previewUrl', label: 'Preview image', type: 'image' },
    { name: 'owner', label: 'Owner', type: 'text' },
    { name: 'restricted', label: 'Restricted', type: 'checkbox', help: 'Never served by the public API - visible here only.' },
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 3 },
    { name: 'tags', label: 'Tags', type: 'tag-chips', wide: true, placeholder: 'Type and press Enter…' },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
  ],
};

const banner: ResourceDescriptor = {
  key: 'banners',
  extraAction: { label: 'Placement', to: '/banners/placement' },
  path: '/private/banners',
  permission: 'banner',
  title: 'Banners',
  description: 'Homepage and section banners.',
  breadcrumb: 'Banners',
  singular: 'banner',
  labelField: 'title',
  publishable: true,
  columns: [
    { key: 'title', label: 'Banner' },
    { key: 'slug', label: 'Slug', muted: true },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'title', label: 'Title', type: 'text', required: true },
    {
      name: 'imageUrl',
      label: 'Image or video',
      type: 'image',
      mediaKind: 'media',
      aspectRatio: BANNER_ASPECT,
      lockAspect: true,
      helpPoints: [
        `Shape: 54:13 - export at ${BANNER_PIXELS} px, or any multiple of 1080 x 260.`,
        'A photo is cropped to that shape in the next step, so a larger file is fine.',
        'A video is never cropped: it is fitted whole into the plate, so a 16:9 clip keeps ' +
          'its shape and is bordered either side rather than trimmed.',
        'Video: MP4 or WebM, under 20 MB, no sound needed - it plays muted and on loop.',
        'The slideshow holds a video slide for the length of the clip, and a photo for 7 seconds.',
      ],
    },
    { name: 'imageAlt', label: 'Image alt text', type: 'text' },
    {
      name: 'linkUrl',
      label: 'Link',
      type: 'text',
      helpPoints: [
        'Where the banner sends a reader who clicks it. Leave empty and the banner is not clickable.',
        'A page of this portal: start with a slash, e.g. /highlights/our-new-rig.',
        'Somewhere else: paste the full address, e.g. https://example.com. It opens in a new tab.',
      ],
    },
    displayOrderField,
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 3 },
  ],
};

const faq: ResourceDescriptor = {
  key: 'faqs',
  path: '/private/faqs',
  permission: 'faq',
  title: 'FAQ',
  description: 'Questions grouped by category on the support page. Answers accept light HTML formatting.',
  breadcrumb: 'FAQ',
  singular: 'entry',
  labelField: 'question',
  publishable: true,
  filters: [{ name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS }],
  columns: [
    { key: 'question', label: 'Question' },
    { key: 'category', label: 'Category', kind: 'badge' },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'question', label: 'Question', type: 'text', required: true, wide: true },
    { name: 'category', label: 'Category', type: 'text', required: true },
    displayOrderField,
    { name: 'answer', label: 'Answer', type: 'textarea', required: true, wide: true, rows: 6 },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
  ],
};

export const quote: ResourceDescriptor = {
  key: 'quotes',
  path: '/private/quotes',
  permission: 'quote',
  title: 'Quotes',
  description: 'Standalone pull-quotes, optionally scoped to an operating entity.',
  breadcrumb: 'Quotes',
  singular: 'quote',
  labelField: 'title',
  publishable: true,
  filters: [
    { name: 'entityCode', label: 'Entity', options: ENTITY_OPTIONS },
    { name: 'employeeCode', label: 'Employee', options: [], dynamic: 'employee-leader' },
    { name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS },
  ],
  columns: [
    { key: 'title', label: 'Quote' },
    { key: 'entityCode', label: 'Entity', kind: 'badge' },
    { key: 'employeeCode', label: 'Employee', muted: true },
    { key: 'featured', label: 'Featured', kind: 'badge' },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'title', label: 'Title', type: 'text', wide: true },
    { name: 'text', label: 'Quote text', type: 'textarea', required: true, wide: true, rows: 3 },
    { name: 'previewText', label: 'Preview text', type: 'textarea', wide: true, rows: 2 },
    {
      name: 'entityCode',
      label: 'Entity',
      type: 'select',
      options: [{ value: '', label: 'Group-wide' }, ...ENTITY_OPTIONS],
    },
    displayOrderField,
    {
      name: 'employeeCode',
      label: 'Attributed to',
      type: 'employee-select',
      help: 'Only employees with role Leader can be attributed. Leave blank for an unattributed quote.',
    },
    { name: 'backgroundImageUrl', label: 'Background image', type: 'image', wide: true },
    { name: 'backgroundImageAlt', label: 'Background image alt text', type: 'text', wide: true },
    { name: 'previewImageUrl', label: 'Preview image', type: 'image', wide: true },
    { name: 'previewImageAlt', label: 'Preview image alt text', type: 'text', wide: true },
    { name: 'showImage', label: 'Show image', type: 'checkbox' },
    { name: 'featured', label: 'Featured', type: 'checkbox', section: 'publication' },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
  ],
};

export const facility: ResourceDescriptor = {
  key: 'facilities',
  path: '/private/facilities',
  permission: 'facility',
  title: 'Facilities',
  description: 'Facilities belonging to an asset - shown as a group on the asset\'s detail page.',
  breadcrumb: 'Facilities',
  singular: 'facility',
  labelField: 'title',
  publishable: true,
  filters: [{ name: 'assetId', label: 'Asset', options: [], dynamic: 'asset' }],
  columns: [
    { key: 'imageUrl', label: 'Image', kind: 'image' },
    { key: 'title', label: 'Title' },
    { key: 'asset.name', label: 'Asset', muted: true },
    { key: 'featured', label: 'Featured', kind: 'badge' },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'assetId', label: 'Asset', type: 'select', required: true, options: [] },
    { name: 'title', label: 'Title', type: 'text', required: true },
    { name: 'subtitle', label: 'Subtitle', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 3 },
    { name: 'specification', label: 'Specification', type: 'textarea', wide: true, rows: 3 },
    { name: 'imageUrl', label: 'Image', type: 'image', wide: true },
    { name: 'imageAlt', label: 'Image alt text', type: 'text', wide: true },
    { name: 'featured', label: 'Featured', type: 'checkbox', section: 'publication' },
    displayOrderField,
  ],
};

export const gallery: ResourceDescriptor = {
  key: 'galleries',
  path: '/private/galleries',
  permission: 'gallery',
  title: 'Galleries',
  description: 'Image sets used across the site.',
  breadcrumb: 'Galleries',
  singular: 'gallery',
  labelField: 'title',
  publishable: true,
  filters: [
    {
      name: 'category',
      label: 'Category',
      options: [
        { value: 'Education', label: 'Education' },
        { value: 'Environment', label: 'Environment' },
        { value: 'Social', label: 'Social' },
      ],
    },
    { name: 'entityCode', label: 'Entity', options: ENTITY_OPTIONS },
    {
      name: 'menu',
      label: 'Menu',
      options: [
        { value: 'COMMUNITY', label: 'Community' },
        { value: 'THROUGH_OUR_LENS', label: 'Through Our Lens' },
      ],
    },
    { name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS },
  ],
  columns: [
    { key: 'title', label: 'Gallery' },
    { key: 'category', label: 'Category', kind: 'badge' },
    { key: 'entityCode', label: 'Entity', kind: 'badge' },
    { key: 'slug', label: 'Slug', muted: true },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'title', label: 'Title', type: 'text', required: true },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: [
        { value: '', label: 'None' },
        { value: 'Education', label: 'Education' },
        { value: 'Environment', label: 'Environment' },
        { value: 'Social', label: 'Social' },
      ],
    },
    {
      name: 'entityCode',
      label: 'Entity',
      type: 'select',
      options: [{ value: '', label: 'Group-wide' }, ...ENTITY_OPTIONS],
    },
    {
      name: 'menu',
      label: 'Menu',
      type: 'select',
      options: [
        { value: '', label: 'None' },
        { value: 'COMMUNITY', label: 'Community' },
        { value: 'THROUGH_OUR_LENS', label: 'Through Our Lens' },
      ],
    },
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 3 },
    displayOrderField,
  ],
};

const organization: ResourceDescriptor = {
  key: 'organizations',
  path: '/private/organizations',
  permission: 'organization',
  title: 'Organizations',
  description:
    'Master data for the stakeholder directory - government bodies, regulators and partners. Reusable: the same organization can be placed into multiple stakeholder trees.',
  breadcrumb: 'Organizations',
  singular: 'organization',
  labelField: 'name',
  publishable: false,
  filters: [{ name: 'category', label: 'Category', options: STAKEHOLDER_CATEGORY_OPTIONS }],
  columns: [
    { key: 'logoUrl', label: 'Logo', kind: 'image' },
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category', kind: 'badge' },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'select', required: true, options: STAKEHOLDER_CATEGORY_OPTIONS },
    { name: 'logoUrl', label: 'Logo', type: 'image' },
    { name: 'logoAlt', label: 'Logo alt text', type: 'text' },
    { name: 'websiteUrl', label: 'Website', type: 'text', placeholder: 'https://…' },
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 3 },
  ],
};

const stakeholder: ResourceDescriptor = {
  key: 'stakeholders',
  path: '/private/stakeholders',
  permission: 'stakeholder',
  title: 'Stakeholder tree',
  description:
    'One page per entity (or "Group-wide"), built by placing organizations into a category and nesting them to form a hierarchy - e.g. Government of Indonesia → East Java → a regency.',
  breadcrumb: 'Stakeholders',
  singular: 'tree',
  labelField: 'entityCode',
  publishable: true,
  filters: [
    { name: 'entityCode', label: 'Entity', options: GROUP_ENTITY_OPTIONS },
    { name: 'visibility', label: 'Visibility', options: VISIBILITY_OPTIONS },
  ],
  columns: [
    { key: 'entityCode', label: 'Entity', kind: 'badge' },
    { key: 'visibility', label: 'Visibility', kind: 'badge' },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    {
      name: 'entityCode',
      label: 'Entity',
      type: 'select',
      required: true,
      options: GROUP_ENTITY_OPTIONS,
      help: 'Group-wide is the general page; a specific entity gets its own standalone tree.',
    },
    { name: 'visibility', label: 'Visibility', type: 'select', required: true, options: VISIBILITY_OPTIONS },
    { name: 'tree', label: 'Organizations', type: 'stakeholder-tree', wide: true },
  ],
};

const employeeService: ResourceDescriptor = {
  key: 'employee-services',
  path: '/private/employee-services',
  permission: 'employeeService',
  title: 'Employee services',
  description:
    'The app-launcher directory shown on the intranet - internal systems and portals employees use day to day.',
  breadcrumb: 'Employee Services',
  singular: 'service',
  labelField: 'title',
  publishable: true,
  filters: [{ name: 'entityCode', label: 'Entity', options: ENTITY_OPTIONS }],
  columns: [
    { key: 'logoUrl', label: 'Logo', kind: 'image' },
    { key: 'title', label: 'Title' },
    { key: 'entityCodes', label: 'Entity' },
    { key: 'featured', label: 'Featured', kind: 'badge' },
    { key: 'displayOrder', label: 'Order', align: 'right', muted: true, sortable: true },
    { key: 'status', label: 'Status', kind: 'status' },
  ],
  fields: [
    { name: 'title', label: 'Title', type: 'text', required: true, placeholder: 'myLearning' },
    { name: 'description', label: 'Description', type: 'textarea', wide: true, rows: 2 },
    { name: 'logoUrl', label: 'Logo', type: 'image' },
    { name: 'logoAlt', label: 'Logo alt text', type: 'text' },
    { name: 'webUrl', label: 'Link', type: 'text', placeholder: 'https://…' },
    {
      name: 'entityCodes',
      label: 'Entity',
      type: 'multi-select',
      options: ENTITY_OPTIONS,
      help: 'Leave empty to offer it group-wide, across all entities.',
    },
    { name: 'featured', label: 'Featured', type: 'checkbox', section: 'publication' },
    displayOrderField,
  ],
};

const auditLog: ResourceDescriptor = {
  key: 'audit-logs',
  path: '/private/audit-logs',
  permission: 'audit',
  title: 'Audit log',
  description: 'Who changed what, and when. Read-only by design - the trail has no write endpoint.',
  breadcrumb: 'Audit log',
  singular: 'entry',
  labelField: 'summary',
  publishable: false,
  readOnly: true,
  columns: [
    { key: 'createdAt', label: 'When' },
    { key: 'userEmail', label: 'User' },
    { key: 'action', label: 'Action', kind: 'badge' },
    { key: 'resource', label: 'Resource', muted: true },
    { key: 'summary', label: 'Summary', muted: true },
  ],
  fields: [],
};

export const RESOURCES: ResourceDescriptor[] = [
  resource,
  banner,
  faq,
  entity,
  asset,
  employee,
  leadershipGroup,
  organization,
  stakeholder,
  employeeService,
  auditLog,
];

export const RESOURCE_BY_KEY: Record<string, ResourceDescriptor> = Object.fromEntries(
  RESOURCES.map((descriptor) => [descriptor.key, descriptor]),
);

function emptyFieldValue(type: FieldType): unknown {
  switch (type) {
    case 'checkbox':
      return false;
    case 'tag-chips':
    case 'multi-select':
    case 'repeater':
    case 'paragraph-list':
      return [];
    case 'stakeholder-tree':
      return { GOVERNMENT_BODY: [], REGULATORY_BODY: [], PARTNER: [] };
    default:
      return '';
  }
}

export function emptyValues(descriptor: ResourceDescriptor): Record<string, unknown> {
  const values: Record<string, unknown> = { status: 'DRAFT' as ContentStatus };

  for (const field of descriptor.fields) {
    values[field.name] = emptyFieldValue(field.type);
  }

  return values;
}
